/**
 * Supabase Client with Clerk Authentication
 *
 * This module provides an authenticated Supabase client that uses Clerk JWT tokens.
 *
 * Authentication Flow:
 * 1. User authenticates with Clerk
 * 2. Clerk issues JWT with user.id in 'sub' claim
 * 3. Frontend requests Supabase-compatible JWT from Clerk
 * 4. Supabase validates JWT and enforces RLS policies
 *
 * Usage:
 * ```typescript
 * import { createSupabaseClient } from '@/lib/supabaseClient';
 *
 * const supabase = await createSupabaseClient();
 * const { data, error } = await supabase.from('eulogies').select('*');
 * ```
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '@clerk/clerk-react';

// Environment variables
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables. Check your .env file.');
}

/**
 * Creates an authenticated Supabase client using Clerk JWT
 *
 * IMPORTANT: This function is async because it needs to fetch the Clerk token.
 * Always await this function before using the client.
 *
 * @returns Promise<SupabaseClient> - Authenticated Supabase client
 * @throws Error if user is not authenticated or token fetch fails
 */
export async function createSupabaseClient(): Promise<SupabaseClient> {
  // Get Clerk token - this must be called from a component inside ClerkProvider
  const { getToken, isSignedIn } = useAuth();

  if (!isSignedIn) {
    throw new Error('User must be signed in to access Supabase');
  }

  // Get Supabase-compatible JWT from Clerk
  // Note: Requires Clerk JWT template named "supabase" (see setup docs)
  const supabaseAccessToken = await getToken({ template: 'supabase' });

  if (!supabaseAccessToken) {
    throw new Error('Failed to get Supabase access token from Clerk');
  }

  // Create Supabase client with Clerk JWT
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    global: {
      headers: {
        Authorization: `Bearer ${supabaseAccessToken}`,
      },
    },
    auth: {
      persistSession: false, // Clerk handles session management
    },
  });

  return supabase;
}

/**
 * Creates a Supabase client for server-side operations (Service Role)
 *
 * WARNING: This bypasses RLS! Only use in serverless functions where you
 * manually enforce security checks.
 *
 * @returns SupabaseClient - Supabase client with service role access
 */
export function createServiceRoleClient(): SupabaseClient {
  const SUPABASE_SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

  if (!SUPABASE_SERVICE_ROLE_KEY) {
    throw new Error('Missing SUPABASE_SERVICE_ROLE_KEY - this should only be used server-side');
  }

  return createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY, {
    auth: {
      persistSession: false,
    },
  });
}

/**
 * Type-safe database schema
 *
 * Import this type to get autocomplete for your database queries:
 * ```typescript
 * const supabase = await createSupabaseClient();
 * const { data } = await supabase.from('eulogies').select('*');
 * // 'data' will be properly typed based on your schema
 * ```
 */
export type Database = {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          clerk_id: string;
          email: string;
          full_name: string | null;
          avatar_url: string | null;
          subscription_status: string;
          subscription_type: string | null;
          subscription_id: string | null;
          subscription_starts_at: string | null;
          subscription_ends_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['users']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['users']['Insert']>;
      };
      eulogies: {
        Row: {
          id: string;
          owner_id: string;
          title: string;
          content: string | null;
          deceased_name: string | null;
          deceased_birth_date: string | null;
          deceased_death_date: string | null;
          relationship: string | null;
          tone: string | null;
          length: string | null;
          status: string;
          version: number;
          collaboration_enabled: boolean;
          created_at: string;
          updated_at: string;
          completed_at: string | null;
        };
        Insert: Omit<Database['public']['Tables']['eulogies']['Row'], 'id' | 'created_at' | 'updated_at' | 'version'>;
        Update: Partial<Database['public']['Tables']['eulogies']['Insert']>;
      };
      // Add other tables as needed
    };
    Views: {};
    Functions: {
      current_user_id: {
        Args: {};
        Returns: string;
      };
    };
  };
};
